import {defineStore} from 'pinia';
import {ref} from 'vue';
import type {CrossFilterEmission, CrossFilterHighlight} from "@/components/bi/biChartTypes.ts";
import FieldTypesUtils from "@/utils/common/FieldTypesUtils.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;
import QuestionField = namaTypes.QuestionField;
import BIWidgetSlotOverride = namaTypes.BIWidgetSlotOverride;

export const useCrossFilterStore = defineStore('crossFilter', () => {
  const questionFields = ref<QuestionField[]>([]);
  const filledQuestions = ref<Map<string, DTOFilledQuestionField>>(new Map());
  const localFilledQuestions = ref<Map<string, Map<string, DTOFilledQuestionField>>>(new Map());
  const pendingRefetches = ref<Set<string>>(new Set());
  const widgetHighlights = ref<Map<string, CrossFilterHighlight>>(new Map());
  const widgetSlotOverrides = ref<Map<string, BIWidgetSlotOverride>>(new Map());

  function init(fields: QuestionField[]) {
    questionFields.value = fields ?? [];
  }

  function findQuestionField(crossFilterId: string): QuestionField | undefined {
    return questionFields.value.find(q => q.id === crossFilterId);
  }

  function setFilter(crossFilterId: string, value: string, sourceChart?: string) {
    filledQuestions.value.set(crossFilterId, {id: crossFilterId, value, sourceChart});
  }

  function setLocalFilter(widgetId: string, crossFilterId: string, value: string) {
    let widgetMap = localFilledQuestions.value.get(widgetId);
    if (!widgetMap) {
      widgetMap = new Map();
      localFilledQuestions.value.set(widgetId, widgetMap);
    }
    widgetMap.set(crossFilterId, {id: crossFilterId, value, localToChart: widgetId});
  }

  function clearFilter(crossFilterId: string) {
    const filter = filledQuestions.value.get(crossFilterId);
    if (filter?.sourceChart)
      widgetHighlights.value.delete(filter.sourceChart);
    filledQuestions.value.delete(crossFilterId);
  }

  function clearLocalFilter(widgetId: string, crossFilterId: string) {
    const widgetMap = localFilledQuestions.value.get(widgetId);
    if (!widgetMap) return;
    widgetMap.delete(crossFilterId);
    if (widgetMap.size === 0)
      localFilledQuestions.value.delete(widgetId);
  }

  function clearAllLocalFiltersFor(widgetId: string) {
    localFilledQuestions.value.delete(widgetId);
  }

  function clearAllFilters() {
    filledQuestions.value.clear();
    localFilledQuestions.value.clear();
    widgetHighlights.value.clear();
  }

  function restoreFilter(filter: DTOFilledQuestionField) {
    if (!filter.id || !filter.value) return;
    if (filter.localToChart)
      setLocalFilter(filter.localToChart, filter.id, filter.value);
    else
      setFilter(filter.id, filter.value, filter.sourceChart);
  }

  function getLocalFilters(widgetId: string | undefined): DTOFilledQuestionField[] {
    if (!widgetId) return [];
    const widgetMap = localFilledQuestions.value.get(widgetId);
    if (!widgetMap) return [];
    return Array.from(widgetMap.values());
  }

  function localFilterCount(widgetId: string | undefined): number {
    if (!widgetId) return 0;
    const widgetMap = localFilledQuestions.value.get(widgetId);
    return widgetMap ? widgetMap.size : 0;
  }

  function getFilledQuestionFields(): DTOFilledQuestionField[] {
    const result: DTOFilledQuestionField[] = Array.from(filledQuestions.value.values());
    for (const widgetMap of localFilledQuestions.value.values())
      result.push(...widgetMap.values());
    return result;
  }

  function getGlobalFilledQuestionFields(): DTOFilledQuestionField[] {
    return Array.from(filledQuestions.value.values());
  }

  function clearGlobalFilters() {
    filledQuestions.value.clear();
    widgetHighlights.value.clear();
  }

  function hasActiveFilters(): boolean {
    if (filledQuestions.value.size > 0) return true;
    for (const widgetMap of localFilledQuestions.value.values())
      if (widgetMap.size > 0) return true;
    return false;
  }

  function addPendingWidget(widgetId: string) {
    pendingRefetches.value.add(widgetId);
  }

  function removePendingWidget(widgetId: string) {
    pendingRefetches.value.delete(widgetId);
  }

  function canEmitCrossFilter(): boolean {
    return pendingRefetches.value.size === 0;
  }

  function buildSerializedValue(emission: CrossFilterEmission, fieldType: string | undefined): string {
    if (fieldType === FieldType.Reference || fieldType === FieldType.Genericreference) {
      if (!emission.id && !emission.entityType && !emission.code && !emission.name1 && !emission.name2)
        return "";
      return FieldTypesUtils.toString(fieldType, {
        id: emission.id ?? "",
        entityType: emission.entityType ?? "",
        code: emission.code ?? "",
        actualCode: "",
        name1: emission.name1 ?? "",
        name2: emission.name2 ?? "",
      });
    }
    return emission.id ?? emission.value ?? "";
  }

  function emitFromChart(widgetId: string, emissions: CrossFilterEmission[]) {
    for (const e of emissions) {
      if (!e.crossFilterCode) continue;
      const questionField = findQuestionField(e.crossFilterCode);
      const fieldType = questionField?.fieldType;
      const serializedValue = buildSerializedValue(e, fieldType);

      const existing = filledQuestions.value.get(e.crossFilterCode);
      if (existing?.value === serializedValue && existing?.sourceChart === widgetId) {
        filledQuestions.value.delete(e.crossFilterCode);
        widgetHighlights.value.delete(widgetId);
      } else {
        filledQuestions.value.set(e.crossFilterCode, {
          id: e.crossFilterCode,
          value: serializedValue,
          sourceChart: widgetId,
        });
        if (e.dataIndex !== undefined)
          widgetHighlights.value.set(widgetId, {seriesIndex: e.seriesIndex, dataIndex: e.dataIndex});
      }
    }
  }

  function isChartSource(widgetId: string | undefined): boolean {
    return getFiltersBySource(widgetId).length > 0;
  }

  function getFiltersBySource(widgetId: string | undefined): DTOFilledQuestionField[] {
    if (!widgetId) return [];
    const result: DTOFilledQuestionField[] = [];
    for (const filter of filledQuestions.value.values()) {
      if (filter.sourceChart === widgetId)
        result.push(filter);
    }
    return result;
  }

  function getHighlight(widgetId: string | undefined): CrossFilterHighlight | undefined {
    if (!widgetId) return undefined;
    return widgetHighlights.value.get(widgetId);
  }

  function setWidgetSlotOverride(widgetId: string, override: BIWidgetSlotOverride) {
    widgetSlotOverrides.value.set(widgetId, {...override, widgetId});
  }

  function resetWidgetSlotOverride(widgetId: string) {
    widgetSlotOverrides.value.delete(widgetId);
  }

  function getWidgetSlotOverride(widgetId: string | undefined): BIWidgetSlotOverride | undefined {
    if (!widgetId) return undefined;
    return widgetSlotOverrides.value.get(widgetId);
  }

  function getAllWidgetSlotOverrides(): BIWidgetSlotOverride[] {
    return Array.from(widgetSlotOverrides.value.values());
  }

  return {
    questionFields, filledQuestions, localFilledQuestions, pendingRefetches, widgetHighlights, widgetSlotOverrides,
    init, setFilter, setLocalFilter, clearFilter, clearLocalFilter, clearAllLocalFiltersFor, clearAllFilters,
    restoreFilter, getLocalFilters, localFilterCount,
    getFilledQuestionFields, getGlobalFilledQuestionFields, clearGlobalFilters,
    hasActiveFilters, findQuestionField,
    addPendingWidget, removePendingWidget, canEmitCrossFilter,
    emitFromChart, isChartSource, getFiltersBySource, getHighlight,
    setWidgetSlotOverride, resetWidgetSlotOverride, getWidgetSlotOverride, getAllWidgetSlotOverrides
  };
});
